"use client"

import clsx from "clsx"

import { Accordion, AccordionPanel } from "@unlikelystudio/react-abstract-components"

import type { PropsWithClassName } from "~/@types/generics"
import { HtmlRichText } from "~/components/ui/HtmlRichText"
import type { TProductHeader } from "~/components/ui/Product/ProductHeader/_data/serializer"
import { StyledWysiwyg } from "~/components/ui/StyledWysiwyg"
import Icon from "~/components/abstracts/Icon"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { fromBezierToArray } from "~/utils/from-bezier-to-array"

import { sprinkles } from "~/styles/sprinkles.css"
import { easings } from "~/styles/variables/easings"

import * as css from "./styles.css"

export type ProductDetailsAccordionsProps = PropsWithClassName<{
  accordions: TProductHeader["accordions"]
}>

export function ProductDetailsAccordions({ className, accordions }: ProductDetailsAccordionsProps) {
  const t = useTranslate()

  return accordions && accordions.length > 0 ? (
    <Accordion
      index={0}
      className={clsx(className, css.ProductDetailsAccordions)}
      data-comp="UI/Product/ProductHeader/ProductDetails/ProductDetailsAccordions"
    >
      {accordions?.map((accordion, index) => {
        return (
          accordion && (
            <AccordionPanel
              key={`product_detail_accordion_${index}`}
              transition={{ duration: 0.5, ease: fromBezierToArray(easings["easeInOutAccordion"]) }}
              className={css.panel}
              headClassName={clsx(css.panelHead)}
              bodyClassName={clsx(css.panelBody)}
              ariaLabels={{
                open: t("aria_nav_mobile_accordion_open", { title: accordion.title }),
                close: t("aria_nav_mobile_accordion_close", { title: accordion.title }),
              }}
              index={index}
              head={(isActive) => (
                <>
                  {accordion.title} <Icon name={isActive ? "ChevronUp" : "ChevronDown"} width={16} />
                </>
              )}
              body={() =>
                accordion.content ? (
                  <div className={clsx(css.content, sprinkles({ mB: 20 }))}>
                    <StyledWysiwyg theme="small">
                      <HtmlRichText>{accordion.content}</HtmlRichText>
                    </StyledWysiwyg>
                  </div>
                ) : null
              }
            />
          )
        )
      })}
    </Accordion>
  ) : null
}
